import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Newspaper, Mail, Download, Quote, Copy, Check, ExternalLink, BookOpen } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import CiteThisPage from "../components/CiteThisPage";
import { usePageSEO } from "../hooks/usePageSEO";

const BASE = "https://www.peptidesformedspas.org";

/**
 * /press — Press & Media Kit page.
 *
 * A canonical "linkable asset" page every serious editorial site has:
 * boilerplate, expert quotes for reporters, editorial contact, media
 * inquiry form, and downloadable fact sheet references. Journalists and
 * PR databases (HARO/Qwoted/SourceBottle successors) link directly to
 * pages structured exactly like this.
 */

const BOILERPLATE = `PeptidesForMedSpas is an educational storefront and research-literacy resource for the peptides most often discussed in med spa, aesthetic, longevity, recovery, hormone, and sexual-wellness research contexts. The site organizes 11 research-grade peptides across five evidence-led categories, paired with plain-English science, a three-tier evidence framework (FDA-approved · compounded · research-only), and state-by-state medical-practice context for all 50 U.S. states + District of Columbia. PeptidesForMedSpas is not a med spa, pharmacy, or medical provider and does not publish dosing, treatment protocols, or disease claims.`;

const QUICK_FACTS = [
  { k: "Founded", v: "2024" },
  { k: "Entity", v: "PeptidesForMedSpas LLC (Hawaii)" },
  { k: "Headquarters", v: "Honolulu, HI" },
  { k: "Editorial contact", v: "everything@8eight8.net" },
  { k: "Curated catalog", v: "11 research-grade peptides across 5 categories" },
  { k: "State pages", v: "50 states + District of Columbia" },
  { k: "Blog library", v: "23 evidence-led articles" },
  { k: "Compliance posture", v: "FDA · FTC · DEA · DSHEA · HIPAA · State med-spa · 503A/503B" },
];

const QUOTES = [
  {
    id: "framework",
    topic: "Why an evidence-tier framework matters",
    quote:
      "The peptide space is drowning in vague marketing. A three-tier framework — FDA-approved pharmaceuticals, compounded preparations, and research-only compounds — lets any reader instantly place a product in a regulatory reality without having to be a pharmacologist.",
    attribution: "PeptidesForMedSpas Editorial",
  },
  {
    id: "medspa",
    topic: "The single riskiest claim in med spa peptide marketing",
    quote:
      "Any med spa that markets a research-only peptide next to language borrowed from an FDA-approved GLP-1 receptor agonist is walking straight into an FTC or state medical-board enforcement lane. Category confusion is the enforcement magnet.",
    attribution: "PeptidesForMedSpas Editorial",
  },
  {
    id: "pt141",
    topic: "PT-141 versus the FDA-approved bremelanotide product",
    quote:
      "PT-141 sold as a research chemical is not the FDA-approved bremelanotide product. Same chemical lineage, entirely different product class and regulatory status. Treating them as interchangeable in a consumer-facing setting is a compliance failure.",
    attribution: "PeptidesForMedSpas Editorial",
  },
  {
    id: "aeo",
    topic: "How AI-search visibility changes the peptide category",
    quote:
      "When ChatGPT, Perplexity, and Google's AI Overviews cite peptide information, they are citing whatever content is (a) rendered as static HTML, (b) unambiguous about regulatory tier, and (c) willing to say the compound has limited human evidence. Sites that hide behind hype get filtered out.",
    attribution: "PeptidesForMedSpas Editorial",
  },
];

const MEDIA_ASSETS = [
  { name: "Logo (PNG, favicon)", href: "/favicon.png" },
  { name: "Open Graph image (1200×630)", href: "/og-image.jpg" },
  { name: "AI context file (JSON)", href: "/ai-context.json" },
  { name: "llms.txt (LLM information file)", href: "/llms.txt" },
  { name: "sitemap.xml", href: "/sitemap.xml" },
];

const Quotable = ({ q }) => {
  const [copied, setCopied] = useState(false);
  const text = `"${q.quote}" — ${q.attribution}, PeptidesForMedSpas`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      /* clipboard unavailable — no user notification needed for a copy convenience */
    }
  };
  return (
    <article
      className="bg-white border border-[#f0d8d4] rounded-2xl p-6"
      data-testid={`press-quote-${q.id}`}
    >
      <p className="text-xs font-bold text-[#b89a6d] uppercase tracking-wider mb-2">{q.topic}</p>
      <blockquote className="text-base md:text-lg text-[#3a2a26] leading-relaxed mb-3">
        <Quote className="w-5 h-5 text-[#d4a5ad] inline-block mr-1 -mt-1" />
        {q.quote}
      </blockquote>
      <div className="flex items-center justify-between pt-3 border-t border-[#f0d8d4]">
        <cite className="text-sm text-[#7a6b62] not-italic">— {q.attribution}</cite>
        <button
          type="button"
          onClick={copy}
          data-testid={`press-quote-copy-${q.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#fdf2f4] hover:bg-[#f0d8d4] text-[#b89a6d] text-xs font-semibold rounded-full transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy quote"}
        </button>
      </div>
    </article>
  );
};

const PressPage = () => {
  const [copied, setCopied] = useState(false);
  usePageSEO();

  const copyBoiler = async () => {
    try {
      await navigator.clipboard.writeText(BOILERPLATE);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      /* clipboard unavailable — no user notification needed */
    }
  };

  const pageUrl = `${BASE}/press`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Press & Media Kit — PeptidesForMedSpas",
      description:
        "Editorial contact, boilerplate, expert quotes, and media assets for journalists and researchers covering peptide research, med spa regulation, and AI-search visibility.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      about: [
        { "@type": "Thing", name: "Research Peptides" },
        { "@type": "Thing", name: "Med Spa Regulation" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Press", item: pageUrl },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${BASE}/#organization`,
      name: "PeptidesForMedSpas",
      url: BASE,
      logo: `${BASE}/favicon.png`,
      email: "everything@8eight8.net",
      description: BOILERPLATE,
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="press-page">
      <Header />
      <JsonLd id="press-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">Press</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <Newspaper className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">Press & Media Kit</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="press-h1">
            Press & Media
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            Editorial contact, boilerplate, ready-to-quote statements, and media assets for reporters, editors, and researchers covering peptide research, med spa regulation, AI-search visibility, and the compliance edge of the aesthetic-wellness market. Free to quote with attribution.
          </p>
        </div>
      </section>

      {/* Editorial contact */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="bg-[#fdf2f4] border-2 border-[#e8c5b8] rounded-2xl p-7" data-testid="press-contact">
            <div className="flex items-start gap-3 mb-3">
              <Mail className="w-6 h-6 text-[#b89a6d] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26] mb-1">Editorial & media inquiries</h2>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">
                  For interviews, expert commentary, fact-checks, or embargo requests:
                </p>
              </div>
            </div>
            <div className="pl-9">
              <a
                href="mailto:everything@8eight8.net?subject=Media%20inquiry%20-%20PeptidesForMedSpas"
                data-testid="press-contact-email"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a5ad] hover:bg-[#c4949c] text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" /> everything@8eight8.net
              </a>
              <p className="text-xs text-[#7a6b62] mt-3">
                Reporter turnaround: same business day (M-F, 9am-5pm HST). We can provide written statements, on-record quotes, and technical review of peptide- and regulatory-related copy.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Boilerplate */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4">About PeptidesForMedSpas (boilerplate)</h2>
          <article
            className="bg-white border border-[#f0d8d4] rounded-2xl p-6"
            data-testid="press-boilerplate"
          >
            <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-4">{BOILERPLATE}</p>
            <button
              type="button"
              onClick={copyBoiler}
              data-testid="press-boilerplate-copy"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#d4a5ad] hover:bg-[#c4949c] text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy boilerplate"}
            </button>
          </article>
        </div>
      </section>

      {/* Quick facts */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4">Quick facts</h2>
          <dl className="grid sm:grid-cols-2 gap-3" data-testid="press-facts">
            {QUICK_FACTS.map((f) => (
              <div key={f.k} className="bg-[#fffaf5] border border-[#f0d8d4] rounded-lg p-4">
                <dt className="text-xs font-bold text-[#b89a6d] uppercase tracking-wider mb-1">{f.k}</dt>
                <dd className="text-sm text-[#3a2a26] font-medium">{f.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Ready-to-quote statements */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3">Ready-to-quote statements</h2>
            <p className="text-sm md:text-base text-[#5a4a40] max-w-2xl">
              On-record, attributable, and free to publish with a link back to <a href={BASE} className="text-[#b89a6d] underline">peptidesformedspas.org</a>. Click &ldquo;Copy quote&rdquo; to grab the exact wording with attribution.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5" data-testid="press-quotes">
            {QUOTES.map((q) => (
              <Quotable key={q.id} q={q} />
            ))}
          </div>
        </div>
      </section>

      {/* Topics we speak on */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4">Topics we speak to on record</h2>
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="press-topics">
            {[
              "The FDA-approved · compounded · research-only tier framework",
              "GLP-1 category confusion in med spa marketing",
              "PT-141 versus the FDA-approved bremelanotide product",
              "State medical-board delegation and med spa peptide risk",
              "AI-search (AEO/GEO) content standards for regulated categories",
              "FTC affiliate-disclosure standards for wellness storefronts",
              "How med spas evaluate compounding pharmacy partners",
              "Research literacy for aesthetic-industry press coverage",
            ].map((t) => (
              <li
                key={t}
                className="flex items-start gap-2 bg-[#fffaf5] border border-[#f0d8d4] rounded-lg px-4 py-3 text-sm text-[#5a4a40]"
              >
                <ChevronRight className="w-4 h-4 text-[#b89a6d] flex-shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Media assets */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4 flex items-center gap-2">
            <Download className="w-6 h-6 text-[#b89a6d]" /> Media assets
          </h2>
          <ul className="grid sm:grid-cols-2 gap-2" data-testid="press-assets">
            {MEDIA_ASSETS.map((a) => (
              <li key={a.href}>
                <a
                  href={a.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg px-4 py-3 text-sm text-[#3a2a26] font-medium transition-colors"
                >
                  <span>{a.name}</span>
                  <ExternalLink className="w-4 h-4 text-[#b89a6d]" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Backlink assets */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#b89a6d]" /> Reference-worthy resources
          </h2>
          <p className="text-sm md:text-base text-[#5a4a40] max-w-2xl mb-4">
            Free, citation-friendly resources reporters and researchers can link to when covering peptide, med spa, or regulatory topics:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/tools/state-regulatory-checker"
              data-testid="press-link-checker"
              className="block bg-[#fffaf5] border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg p-5 transition-colors"
            >
              <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-1">State Regulatory Checker</h3>
              <p className="text-xs md:text-sm text-[#5a4a40]">
                Interactive tool — pick a state, get the medical board, delegation framework, and med spa peptide context.
              </p>
            </Link>
            <Link
              to="/glossary"
              data-testid="press-link-glossary"
              className="block bg-[#fffaf5] border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg p-5 transition-colors"
            >
              <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-1">Peptide Glossary API</h3>
              <p className="text-xs md:text-sm text-[#5a4a40]">
                Free structured JSON fact sheet per compound. Citation-ready for AI engines, blogs, and reporters.
              </p>
            </Link>
            <Link
              to="/editorial-standards"
              data-testid="press-link-editorial"
              className="block bg-[#fffaf5] border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg p-5 transition-colors"
            >
              <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-1">Editorial Standards</h3>
              <p className="text-xs md:text-sm text-[#5a4a40]">
                Three-tier evidence framework, review methodology, and the list of things we never publish.
              </p>
            </Link>
            <Link
              to="/compliance"
              data-testid="press-link-compliance"
              className="block bg-[#fffaf5] border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-lg p-5 transition-colors"
            >
              <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-1">Compliance Framework</h3>
              <p className="text-xs md:text-sm text-[#5a4a40]">
                Full FDA / FTC / DEA / DSHEA / HIPAA / state med-spa / 503A / 503B posture.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Cite */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CiteThisPage
            title="Press & Media Kit — PeptidesForMedSpas"
            url={pageUrl}
            publishedDate="2026-05-26"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PressPage;
